// Returned by a latestOnly() call that was replaced by a newer call (or
// cancelled) before it finished. Callers should ignore it.
const CANCELLED = Symbol('cancelled')

// Wraps an async task so that only the most recent call counts, like RxJS
// switchMap. The task receives an AbortSignal as its first argument. Each new
// call aborts the previous one, and a replaced call resolves to CANCELLED even
// if its task ignored the signal. Errors from the current call still throw.
//
//   const search = latestOnly((signal, query) => api.search(query, { signal }))
//   const result = await search('102')
//   if (result === CANCELLED) return
//
// search.cancel() aborts the call in flight without starting a new one.
function latestOnly(task) {
  let currentController = null

  const run = async (...args) => {
    if (currentController) currentController.abort()
    const controller = new AbortController()
    currentController = controller

    try {
      const value = await task(controller.signal, ...args)
      return controller.signal.aborted ? CANCELLED : value
    } catch (error) {
      if (controller.signal.aborted) return CANCELLED
      throw error
    } finally {
      if (currentController === controller) currentController = null
    }
  }

  run.cancel = () => {
    if (currentController) {
      currentController.abort()
      currentController = null
    }
  }

  return run
}

export { CANCELLED, latestOnly }
